import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Send, Image as ImageIcon, ChevronDown, ChevronUp, Loader2,
  Settings as SettingsIcon, Box, MessageSquare, Menu, X,
  FileCode, Box as BoxIcon, Copy, Check, ArrowLeft, Trash2,
  AlertTriangle, ShieldAlert, RefreshCw, Briefcase, Bot, User, 
  FileQuestion, ArrowDown, Activity, BrainCircuit, GitCommit, 
  Flag, Zap, Calendar, Clock, Search, Filter, ArrowUp, XCircle
} from 'lucide-react';

import { supabase } from './supabaseClient';
import { projectAssets as mockAssets } from './mockData';
import { Button } from './components/Button';

const PROXY_URL = "https://everydaycats-ai-plugin-server.hf.space";

const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
};

export default function ProjectWorkspace() {
  const navigate = useNavigate();
  const { id } = useParams();

	// Demo (to show assets)
  const [mockA, setMockA] = useState(true); //false
	// Demo

  const [session, setSession] = useState(null);
  const [accessToken, setAccessToken] = useState(null);

  const [activeTab, setActiveTab] = useState('chat');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);

  const [projectInfo, setProjectInfo] = useState({ title: "Loading...", user_id: "", status: "idle" });
  const [messages, setMessages] = useState([]);
  
  // Timeline/Milestone States
  const [milestones, setMilestones] = useState([]);
  const [filteredMilestones, setFilteredMilestones] = useState([]);
  const [isLoadingMilestones, setIsLoadingMilestones] = useState(false);
  
  // Timeline Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [filterDate, setFilterDate] = useState(""); // YYYY-MM-DD

  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("Initializing...");
  const [liveStreamText, setLiveStreamText] = useState("");

  const [chunksToLoad, setChunksToLoad] = useState(2);
  const [showScrollBottom, setShowScrollBottom] = useState(false);

  const [creditError, setCreditError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [generatedKey, setGeneratedKey] = useState(null);
  const [isRolling, setIsRolling] = useState(false);

  const [input, setInput] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);

  const prevStatusRef = useRef("");

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setSession(session);
        setAccessToken(session.access_token);
      } else {
        navigate('/auth');
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setSession(session);
        setAccessToken(session.access_token);
      } else {
        navigate('/auth');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchMessages = useCallback(async () => {
      if (!id) return;
      const { data, error } = await supabase
        .from('message_chunks')
        .select('payload, chunk_index')
        .eq('project_id', id)
        .order('chunk_index', { ascending: false })
        .limit(chunksToLoad);

      if (error) { console.error("Error fetching messages:", error); return; }

      const sortedChunks = (data || []).sort((a, b) => a.chunk_index - b.chunk_index);
      let allMessages = [];
      sortedChunks.forEach(chunk => {
        if (Array.isArray(chunk.payload)) allMessages = [...allMessages, ...chunk.payload];
      });

      const formatted = allMessages.map((msg, index) => ({
        id: index,
        role: msg.role === 'model' ? 'ai' : 'user',
        content: msg.parts?.[0]?.text || "",
        image: null
      }));

      setMessages(formatted);
  }, [id, chunksToLoad]);

  const fetchMilestones = useCallback(async () => {
      if (!id) return;
      setIsLoadingMilestones(true);
      
      const { data, error } = await supabase
          .from('project_milestones')
          .select('*')
          .eq('project_id', id)
          .order('created_at', { ascending: false });

      if (error) {
          console.warn("Could not fetch milestones:", error);
      } else {
          setMilestones(data || []);
      }
      setIsLoadingMilestones(false);
  }, [id]);

  // --- FILTERING LOGIC ---
  useEffect(() => {
      let result = [...milestones]; // Create a copy

      // 1. Search
      if (searchQuery.trim() !== "") {
          const query = searchQuery.toLowerCase();
          result = result.filter(m => 
              m.title.toLowerCase().includes(query) || 
              m.description.toLowerCase().includes(query)
          );
      }

      // 2. Category
      if (selectedCategory !== "all") {
          result = result.filter(m => m.category === selectedCategory);
      }

      // 3. Date Filter (Specific Day)
      if (filterDate) {
          // Normalize the input date and record date to midnight for comparison
          const selectedTime = new Date(filterDate).setHours(0,0,0,0);
          const endSelectedTime = new Date(filterDate).setHours(23,59,59,999);
          
          result = result.filter(m => {
             const recordTime = new Date(m.created_at).getTime();
             return recordTime >= selectedTime && recordTime <= endSelectedTime;
          });
      }

      // 4. Sort Order
      result.sort((a, b) => {
          const timeA = new Date(a.created_at).getTime();
          const timeB = new Date(b.created_at).getTime();
          return sortOrder === 'asc' ? timeA - timeB : timeB - timeA;
      });

      setFilteredMilestones(result);
  }, [milestones, searchQuery, selectedCategory, sortOrder, filterDate]);

  useEffect(() => {
    if (!id) return;
    const initFetch = async () => {
      const { data, error } = await supabase.from('projects').select('info, user_id').eq('id', id).single();
      if (error || !data) { setNotFound(true); }
      else {
        setProjectInfo({ ...data.info, user_id: data.user_id });
        if (data.info?.status === 'working') {
          setIsProcessing(true);
          setCurrentStatus("Resuming session...");
        } else {
          setIsProcessing(false);
          setLiveStreamText("");
        }
        fetchMessages();
        fetchMilestones();
      }
    };
    initFetch();
  }, [id, fetchMessages, fetchMilestones]);

  useEffect(() => {
    if (!id || notFound || !isProcessing || !accessToken) return;

    let interval = setInterval(async () => {
        try {
            const response = await fetch(`${PROXY_URL}/stream-feed`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ projectId: id })
            });

            const data = await response.json();

            if (data.status && data.status !== prevStatusRef.current) {
                if (prevStatusRef.current !== "") {
                    fetchMessages();
                }
                prevStatusRef.current = data.status;
                setCurrentStatus(data.status);
            }

            if (data.snapshot) {
                setLiveStreamText(data.snapshot);
                if (!showScrollBottom) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
            }

            if (data.dbStatus === 'idle' || data.dbStatus === 'error') {
                clearInterval(interval);
                setIsProcessing(false);
                setLiveStreamText("");
                setProjectInfo(prev => ({ ...prev, status: data.dbStatus }));
                fetchMessages();
                fetchMilestones();
            }

        } catch (e) {
            console.warn("Stream Poll Error", e);
        }
    }, 1000);

    return () => clearInterval(interval);
  }, [id, notFound, isProcessing, accessToken, fetchMessages, fetchMilestones, showScrollBottom]);

  // Auto-scroll logic for chat
  /*useEffect(() => {
    if (!showScrollBottom && activeTab === 'chat') {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, liveStreamText, isProcessing, activeTab, showScrollBottom]);
  */


	useEffect(() => {                                        if (!showScrollBottom) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });                      }, [messages, liveStreamText, isProcessing]);                                                                 useLayoutEffect(() => {                                    if (activeTab === 'chat') messagesEndRef.current?.scrollIntoView({ behavior: "auto" });                   }, [activeTab]);

  const handleScroll = (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      // Show button if user is 300px away from bottom
      const isAwayFromBottom = scrollHeight - scrollTop - clientHeight > 300;
      setShowScrollBottom(isAwayFromBottom);
      
      // Load more chunks logic
      if (scrollTop === 0 && messages.length > 0) setChunksToLoad(prev => prev + 2);
  };

  const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setShowScrollBottom(false);
  };

  const generatePluginKey = async () => {
    if (!accessToken) return;
    setIsRolling(true);
    try {
        const response = await fetch(`${PROXY_URL}/key`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
            body: JSON.stringify({ projectId: id })
        });
        const data = await response.json();
        if (data.key) setGeneratedKey(data.key);
    } catch (e) { console.error(e); }
    finally { setIsRolling(false); }
  };

  const populateDemoMilestones = async () => {
      if (!id) return;
      setIsLoadingMilestones(true);
      
      const demoData = [
          {
              project_id: id,
              title: "Combat System Alpha",
              description: "Implemented basic sword swing mechanics and hit detection logic.",
              category: "feature",
              created_at: new Date(Date.now() - 1000 * 60 * 5).toISOString()
          },
          {
              project_id: id,
              title: "Inventory UI Scaffold",
              description: "Created the ScreenGui layout for the player inventory. No scripts attached yet.",
              category: "ui",
              created_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString()
          },
          {
              project_id: id,
              title: "DataStore Connection",
              description: "Established connection to ProfileService for handling player gold and stats.",
              category: "backend",
              created_at: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString()
          },
          {
              project_id: id,
              title: "Map Greybox Completed",
              description: "Finished the basic layout of the starting town using primitive parts.",
              category: "chore",
              created_at: new Date(Date.now() - 1000 * 60 * 60 * 48).toISOString()
          },
           {
              project_id: id,
              title: "Project Initialized",
              description: "Hollowpad workspace created. Git repository initialized.",
              category: "init",
              created_at: new Date(Date.now() - 1000 * 60 * 60 * 50).toISOString()
          }
      ];

      await supabase.from('project_milestones').delete().eq('project_id', id);
      const { error } = await supabase.from('project_milestones').insert(demoData);
      
      if (error) {
          alert("Error populating demo data: " + error.message);
      } else {
          fetchMilestones();
      }
      setIsLoadingMilestones(false);
  };

  useEffect(() => { if (accessToken && !notFound) generatePluginKey(); }, [accessToken, notFound]);

  const handleSend = async () => {
    if((!input.trim() && !selectedFile) || isProcessing || !session) return;
    setCreditError(null);

    const tempMsg = { id: `temp-${Date.now()}`, role: 'user', content: input, image: previewUrl };
    setMessages(prev => [...prev, tempMsg]);

    const promptText = input;
    const fileToSend = selectedFile;

    setInput("");
    setSelectedFile(null);
    setPreviewUrl(null);

    setIsProcessing(true);
    setProjectInfo(prev => ({ ...prev, status: 'working' }));
    setCurrentStatus("Initializing Request...");
    setLiveStreamText("");
    prevStatusRef.current = "Initializing Request...";

    setTimeout(scrollToBottom, 100);

    try {
        let imagesPayload = [];
        if (fileToSend) {
            const base64 = await convertFileToBase64(fileToSend);
            imagesPayload.push(base64);
        }

        const response = await fetch(`${PROXY_URL}/feedback2`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
            body: JSON.stringify({ projectId: id, prompt: promptText, images: imagesPayload, logContext: null, hierarchyContext: null, scriptContext: null })
        });

        const data = await response.json();
        const payload = data.externalResponse || data;

        if (payload.insufficient) {
            setCreditError(payload.error || "Insufficient Credits. Please upgrade.");
            setIsProcessing(false);
            setProjectInfo(prev => ({ ...prev, status: 'idle' }));
            return;
        }

    } catch (err) {
        setIsProcessing(false);
        setProjectInfo(prev => ({ ...prev, status: 'idle' }));
    }
  };

  const handleConfirmNullify = async () => {
    if (!accessToken) return;
    try {
        await fetch(`${PROXY_URL}/nullify`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ projectId: id })
        });
        setShowSessionModal(false);
        generatePluginKey();
    } catch(e) { console.error(e); }
  };

  const handleDeleteProject = async () => {
     if (!accessToken) return;
     setIsDeleting(true);

     try {
         const res = await fetch(`${PROXY_URL}/project/delete`, {
             method: 'POST',
             headers: {
                 'Content-Type': 'application/json',
                 'Authorization': `Bearer ${accessToken}`
             },
             body: JSON.stringify({ projectId: id })
         });
         const data = await res.json();
         if(data.success) {
             navigate('/console');
         } else {
             alert("Delete Failed: " + (data.error || "Unknown Error"));
             setIsDeleting(false);
         }
     } catch (e) {
         console.error(e);
         alert("Network Error during deletion.");
         setIsDeleting(false);
     }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if(file) {
        setSelectedFile(file);
        setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if(fileInputRef.current) fileInputRef.current.value = "";
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-slate-300 p-4">
          <div className="bg-slate-900 border border-slate-800 p-8 rounded-2xl shadow-2xl text-center max-w-md w-full">
              <h1 className="text-2xl font-bold text-white mb-2">Project Not Found</h1>
              <button onClick={() => navigate('/console')} className="bg-blue-600 text-white font-semibold py-3 px-6 rounded-xl mt-4">Return to Console</button>
          </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-950 text-slate-200 flex overflow-hidden font-sans">
      <aside className="hidden md:flex flex-col w-64 border-r border-slate-800 bg-slate-950 shrink-0 z-30 justify-between">
        <div>
            <div className="h-16 flex items-center gap-3 px-6 border-b border-slate-800/50">
                <img src="/logo512.png" alt="Logo" className="w-8 h-8 rounded-lg object-contain bg-slate-900 border border-slate-800" />
                <span className="font-bold text-lg text-white tracking-tight">Hollowpad</span>
            </div>
            <div className="flex flex-col gap-2 p-4">
                <NavIcon label="Chat" icon={<MessageSquare size={20} />} active={activeTab === 'chat'} onClick={() => setActiveTab('chat')} />
                <NavIcon label="Timeline" icon={<GitCommit size={20} />} active={activeTab === 'timeline'} onClick={() => { setActiveTab('timeline'); fetchMilestones(); }} />
                <NavIcon label="Assets" icon={<Box size={20} />} active={activeTab === 'assets'} onClick={() => setActiveTab('assets')} />
                <NavIcon label="Settings" icon={<SettingsIcon size={20} />} active={activeTab === 'settings'} onClick={() => setActiveTab('settings')} />
            </div>
        </div>
        <div className="p-4 border-t border-slate-800 bg-slate-900/30">
            <button onClick={() => navigate('/console')} className="flex items-center gap-3 px-4 py-3 w-full rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-all group">
                <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                <span className="font-medium">Back to Console</span>
            </button>
        </div>
      </aside>

      {isSidebarOpen && <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 md:hidden" onClick={() => setIsSidebarOpen(false)} />}
      <div className={`fixed inset-y-0 left-0 z-[60] w-72 bg-slate-900 border-r border-slate-800 transform transition-transform duration-300 flex flex-col justify-between ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:hidden`}>
         <div>
            <div className="flex items-center justify-between p-4 border-b border-slate-800 mb-4 h-16">
                <div className="flex items-center gap-3">
                    <img src="/logo512.png" alt="Logo" className="w-8 h-8 rounded-lg object-contain" />
                    <span className="font-bold text-white text-xl">Hollowpad</span>
                </div>
                <button onClick={() => setIsSidebarOpen(false)} className="text-slate-400 p-2"><X size={24} /></button>
            </div>
            <div className="flex flex-col gap-2 px-4">
                <NavIcon label="Chat" icon={<MessageSquare size={24} />} active={activeTab === 'chat'} onClick={() => { setActiveTab('chat'); setIsSidebarOpen(false); }} />
                <NavIcon label="Timeline" icon={<GitCommit size={24} />} active={activeTab === 'timeline'} onClick={() => { setActiveTab('timeline'); setIsSidebarOpen(false); fetchMilestones(); }} />
                <NavIcon label="Assets" icon={<Box size={24} />} active={activeTab === 'assets'} onClick={() => { setActiveTab('assets'); setIsSidebarOpen(false); }} />
                <NavIcon label="Settings" icon={<SettingsIcon size={24} />} active={activeTab === 'settings'} onClick={() => { setActiveTab('settings'); setIsSidebarOpen(false); }} />
            </div>
        </div>
        <div className="p-4 border-t border-slate-800">
             <button onClick={() => navigate('/console')} className="flex items-center gap-4 px-4 py-3 w-full rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white transition-all"><ArrowLeft size={24} /><span className="font-medium text-lg">Back to Console</span></button>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-w-0 h-full relative">
        <header className="h-16 shrink-0 border-b border-slate-800 bg-slate-900 flex items-center px-4 justify-between z-20">
            <div className="flex items-center gap-3">
                <button onClick={() => setIsSidebarOpen(true)} className="md:hidden p-2 text-slate-400 hover:text-white"><Menu size={24} /></button>
                <span className="font-bold text-white text-lg tracking-tight truncate">{projectInfo.title}</span>
            </div>
            <div className="text-xs text-slate-500 font-mono hidden sm:block">ID: {id}</div>
        </header>

        <main className="flex-1 flex flex-col min-h-0 overflow-hidden relative bg-slate-950">
            {activeTab === 'chat' && (
                <div className="flex flex-col h-full w-full relative">
                    <div ref={chatContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4 md:p-8 space-y-6 scroll-smooth min-h-0">
                        {messages.length === 0 && !isProcessing && (
                             <div className="text-center text-slate-600 mt-10"><p>Welcome to <strong>{projectInfo.title}</strong></p></div>
                        )}

                        {messages.map((msg) => {
                            const isUser = msg.role === 'user';
                            const isPM = !isUser && (msg.content.includes('[PM RESPONSE]') || msg.content.includes('PM RESPONSE:'));
                            let bubbleClass = "bg-slate-900 border border-slate-800 text-slate-200 rounded-bl-none";
                            if (isUser) bubbleClass = "bg-[#172554] border border-blue-900/50 text-slate-200 rounded-br-none";
                            else if (isPM) bubbleClass = "bg-[#0a0b14] border border-indigo-500/20 text-indigo-100 rounded-bl-none shadow-lg shadow-indigo-900/10";

                            return (
                                <div key={msg.id} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`max-w-[95%] md:max-w-[85%] p-4 rounded-xl text-sm md:text-base shadow-sm ${bubbleClass}`}>
                                        {msg.image && <img src={msg.image} alt="attachment" className="mb-3 rounded-lg border border-white/10 w-72 h-48 object-cover bg-slate-950 block" />}
                                        <MessageRenderer content={msg.content} />
                                    </div>
                                </div>
                            );
                        })}

                        {isProcessing && (
                            <div className="flex justify-start animate-in fade-in duration-300">
                                <div className="max-w-[95%] md:max-w-[85%] p-4 rounded-xl text-sm md:text-base shadow-sm bg-slate-900/50 border border-blue-500/20 text-slate-200 rounded-bl-none">
                                    <div className="flex items-center gap-2 mb-2 text-blue-400 text-xs font-mono uppercase tracking-wide">
                                        <Activity size={12} className="animate-pulse" /> {currentStatus}
                                    </div>
                                    {liveStreamText ? (
                                        <div className="opacity-90"><MessageRenderer content={liveStreamText} /></div>
                                    ) : (
                                        <div className="h-4 w-24 bg-slate-800/50 rounded animate-pulse" />
                                    )}
                                </div>
                            </div>
                        )}

                        <div ref={messagesEndRef} className="h-1" />
                    </div>

                    {showScrollBottom && (
                        <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-30 animate-in fade-in zoom-in duration-200">
                             <button onClick={scrollToBottom} className="bg-slate-800/90 hover:bg-slate-700 text-blue-400 border border-slate-700 shadow-lg backdrop-blur-sm p-2 rounded-full flex items-center gap-2 px-4 transition-all hover:scale-105">
                                <ArrowDown size={16} /><span className="text-xs font-semibold">Scroll to Bottom</span>
                             </button>
                        </div>
                    )}

                    <div className="shrink-0 bg-slate-900 border-t border-slate-800 p-4 z-20 w-full relative">
                         {creditError && (
                            <div className="mb-3 p-3 bg-red-950/40 border border-red-500/30 rounded-xl flex items-start gap-3 animate-in fade-in slide-in-from-bottom-2">
                                <ShieldAlert size={18} className="text-red-500 mt-0.5 shrink-0" />
                                <div className="flex-1 text-sm text-red-200"><span className="font-bold text-red-400 block mb-0.5">Payment Required</span>{creditError}</div>
                                <button onClick={() => setCreditError(null)} className="text-slate-500 hover:text-white transition-colors"><X size={16} /></button>
                            </div>
                        )}

                        {previewUrl && (
                            <div className="absolute bottom-full left-4 mb-2 bg-slate-800 border border-slate-700 p-2 rounded-xl shadow-xl flex flex-col w-40 animate-in slide-in-from-bottom-2">
                                <div className="relative group"><img src={previewUrl} className="h-24 w-full rounded-lg object-cover" alt="Preview" /><button onClick={clearFile} disabled={isProcessing} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:scale-110 transition-transform"><X size={12} /></button></div>
                            </div>
                        )}

                        <div className={`max-w-4xl mx-auto flex items-end gap-3 bg-slate-950 p-2 rounded-xl border transition-all duration-300 ${isProcessing ? 'border-slate-800 opacity-60 cursor-wait' : 'border-slate-800 shadow-inner focus-within:border-blue-500/50'}`}>
                            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
                            <button onClick={() => fileInputRef.current?.click()} disabled={isProcessing} className="p-3 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 disabled:hover:bg-transparent transition-all"><ImageIcon size={20} /></button>
                            <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()} disabled={isProcessing} placeholder={isProcessing ? "AI is working..." : "Describe what to build..."} className="flex-1 bg-transparent border-none outline-none text-white resize-none max-h-32 py-3 placeholder:text-slate-600 disabled:cursor-not-allowed" rows={1} />
                            <Button onClick={handleSend} disabled={(!input.trim() && !selectedFile) || isProcessing} className="h-10 w-10 p-0 rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-slate-800 disabled:text-slate-500">{isProcessing ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}</Button>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === 'timeline' && (
                <div className="flex-1 overflow-y-auto p-4 md:p-8 min-h-0 bg-slate-950">
                    <div className="max-w-4xl mx-auto">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                            <div>
                                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                                    <GitCommit className="text-blue-500" /> Milestone History
                                </h2>
                                <p className="text-slate-400 text-sm mt-1">Timeline of project achievements and technical decisions.</p>
                            </div>
                           
		    {/*   <Button onClick={populateDemoMilestones} disabled={isLoadingMilestones} className="bg-slate-800 hover:bg-slate-700 text-xs border border-slate-700 whitespace-nowrap self-start md:self-auto">
                                {isLoadingMilestones ? <Loader2 size={14} className="animate-spin mr-1" /> : <Zap size={14} className="mr-1 text-yellow-400" />} 
                                Populate Demo Data
                            </Button>

				*/}
                        </div>
			

                        {/* --- SEARCH & FILTER TOOLBAR --- */}
                        <div className="flex flex-col gap-4 mb-6 bg-slate-900 border border-slate-800 p-4 rounded-xl">
                            {/* Row 1: Search, Sort, Date */}
                            <div className="flex flex-col md:flex-row gap-3">
                                {/* Search */}
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                                    <input 
                                        type="text" 
                                        placeholder="Search milestones..." 
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-lg py-2 pl-10 pr-4 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500/50"
                                    />
                                </div>
                                
                                {/* Date Filter */}
                                <div className="relative">
                                    <div className="flex items-center bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-slate-300 gap-2">
                                        <Calendar size={16} className="text-slate-500" />
                                        <input 
                                            type="date" 
                                            value={filterDate}
                                            onChange={(e) => setFilterDate(e.target.value)}
                                            className="bg-transparent border-none outline-none text-white w-32 placeholder-slate-500"
                                        />
                                        {filterDate && (
                                            <button onClick={() => setFilterDate("")} className="text-slate-500 hover:text-red-400">
                                                <XCircle size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Sort Toggle */}
                                <button 
                                    onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
                                    className="flex items-center justify-center gap-2 bg-slate-800 border border-slate-700 hover:bg-slate-700 text-slate-300 px-4 py-2 rounded-lg text-sm transition-colors whitespace-nowrap"
                                >
                                    {sortOrder === 'desc' ? <ArrowDown size={16} /> : <ArrowUp size={16} />}
                                    {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
                                </button>
                            </div>

                            {/* Row 2: Categories */}
                            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
                                <Filter size={18} className="text-slate-500 shrink-0 hidden md:block" />
                                <div className="h-6 w-px bg-slate-800 mx-2 hidden md:block"></div>
                                {['all', 'feature', 'ui', 'backend', 'chore', 'init'].map(cat => (
                                    <button 
                                        key={cat}
                                        onClick={() => setSelectedCategory(cat)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize whitespace-nowrap transition-colors border ${
                                            selectedCategory === cat 
                                            ? 'bg-blue-600 border-blue-500 text-white' 
                                            : 'bg-slate-800 border-slate-700 text-slate-400 hover:text-white hover:bg-slate-700'
                                        }`}
                                    >
                                        {cat}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {isLoadingMilestones ? (
                            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
                                <Loader2 size={40} className="animate-spin mb-4 text-blue-500" />
                                <p>Syncing Timeline...</p>
                            </div>
                        ) : filteredMilestones.length === 0 ? (
                            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-10 text-center">
                                <Flag size={48} className="mx-auto text-slate-700 mb-4" />
                                <h3 className="text-lg font-medium text-slate-300">
                                    {milestones.length === 0 ? "No milestones yet" : "No results found"}
                                </h3>
                                <p className="text-slate-500 mb-6">
                                    {milestones.length === 0 ? "Start building to generate your project history." : "Try adjusting your search or filters."}
                                </p>
			
		{/*     {milestones.length === 0 && (
                                    <Button onClick={populateDemoMilestones} className="mx-auto bg-blue-600 hover:bg-blue-500">Generate Demo Milestones</Button>
                                )}
				*/}

                            </div>
                        ) : (
                            <div className="relative pl-8 border-l border-slate-800 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                {filteredMilestones.map((milestone, index) => (
                                    <div key={milestone.id || index} className="relative group">
                                        {/* Dot on the timeline */}
                                        <div className={`absolute -left-[41px] top-1 h-5 w-5 rounded-full border-4 border-slate-950 flex items-center justify-center z-10
                                            ${milestone.category === 'feature' ? 'bg-blue-500' : 
                                              milestone.category === 'ui' ? 'bg-purple-500' : 
                                              milestone.category === 'backend' ? 'bg-amber-500' : 'bg-slate-600'}`}>
                                        </div>

                                        <div className="bg-slate-900 border border-slate-800 p-5 rounded-xl hover:border-slate-700 transition-all shadow-sm">
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex items-center gap-3 flex-wrap">
                                                    <h3 className="font-bold text-white text-lg">{milestone.title}</h3>
                                                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full border 
                                                        ${milestone.category === 'feature' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 
                                                          milestone.category === 'ui' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 
                                                          milestone.category === 'backend' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-slate-700/30 text-slate-400 border-slate-600'}`}>
                                                        {milestone.category || 'update'}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1.5 text-xs text-slate-500 font-mono shrink-0">
                                                    <Calendar size={12} />
                                                    {new Date(milestone.created_at).toLocaleDateString()} 
                                                    <span className="opacity-50 mx-1">|</span>
                                                    <Clock size={12} />
                                                    {new Date(milestone.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </div>
                                            </div>
                                            <p className="text-slate-300 leading-relaxed text-sm">{milestone.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {activeTab === 'assets' && (
                <div className="flex-1 overflow-y-auto p-4 md:p-8 min-h-0">
                     <h2 className="text-2xl font-bold mb-6 text-white">Project Assets</h2>

		    {mockA && (

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
		 {mockAssets.map(asset => (
                            <div key={asset.id} className="bg-slate-900 border border-slate-800 p-4 rounded-xl flex items-center gap-4 hover:border-blue-500/30 transition-colors cursor-pointer group">
                                <div className={`p-3 rounded-lg ${asset.type === 'script' ? 'bg-blue-900/20 text-blue-400' : 'bg-orange-900/20 text-orange-400'}`}>{asset.type === 'script' ? <FileCode size={24} /> : <BoxIcon size={24} />}</div>
                                <div><div className="font-semibold text-white group-hover:text-blue-300 transition-colors">{asset.name}</div><div className="text-xs text-slate-500 uppercase">{asset.type} • {asset.size}</div></div>
                            </div>
                        ))} 

                     </div>
		    ) } 

		    {!mockA && (
			    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-10 text-center">                                                                                    <Flag size={48} className="mx-auto text-slate-700 mb-4" />                                                    <h3 className="text-lg font-medium text-slate-300">                                                           No Asset logged yet                                    </h3>                                                  <p className="text-slate-500 mb-6">                                                     Start building or log your assets manually.                                       </p>                                                                                          {/*     {milestones.length === 0 && (                                      <Button onClick={populateDemoMilestones} className="mx-auto bg-blue-600 hover:bg-blue-500">Generate Demo Milestones</Button>                                     )}                                                     */}                                                                                                       </div>
		    )}
                </div>
            )}
            {activeTab === 'settings' && (
                <div className="flex-1 overflow-y-auto p-4 md:p-8 min-h-0">
                    <div className="max-w-3xl mx-auto space-y-6 pb-12">
                        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl transition-all">
                            <div className="flex items-center justify-between mb-2">
                                <h3 className="text-lg font-bold text-white">Plugin Auth Key</h3>
                                <button onClick={generatePluginKey} disabled={isRolling} className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                                    <RefreshCw size={12} className={isRolling ? "animate-spin" : ""} /> Regenerate
                                </button>
                            </div>
                            <p className="text-sm text-slate-400 mb-4">Paste this key into the Roblox Studio plugin to connect this project.</p>
                            <CredentialCopyBox value={generatedKey || "Generating..."} isRolling={isRolling || !generatedKey} />
                        </div>
                         <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-xl">
                            <div className="flex items-center gap-2 mb-2"><ShieldAlert className="text-amber-500" size={20} /><h3 className="text-lg font-bold text-white">Session Management</h3></div>
                            <p className="text-sm text-slate-400 mb-6">Forcefully disconnect all active Roblox Studio sessions if you suspect a key leak.</p>
                            <div className="flex justify-end"><button onClick={() => setShowSessionModal(true)} className="bg-amber-500/10 hover:bg-amber-500/20 text-amber-500 border border-amber-500/50 px-4 py-2 rounded-lg transition-all flex items-center gap-2 text-sm font-semibold"><ShieldAlert size={16} /> Nullify All Sessions</button></div>
                        </div>
                        <div className="bg-red-950/10 border border-red-900/30 rounded-xl p-6">
                            <h3 className="text-lg font-bold text-red-500 flex items-center gap-2 mb-4"><AlertTriangle size={20} /> Danger Zone</h3>
                            <div className="flex justify-end"><button onClick={() => setShowDeleteModal(true)} className="bg-red-600/10 hover:bg-red-600 text-red-500 hover:text-white border border-red-600/50 px-4 py-2 rounded-lg transition-all flex items-center gap-2 text-sm font-semibold"><Trash2 size={16} /> Delete Project</button></div>
                        </div>
                    </div>
                </div>
            )}
        </main>
      </div>
       {showDeleteModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl max-w-md w-full shadow-2xl">
                <div className="flex items-center gap-4 mb-4 text-red-500"><div className="p-3 bg-red-500/10 rounded-full"><AlertTriangle size={32} /></div><h3 className="text-xl font-bold text-white">Delete Project?</h3></div>
                <p className="text-slate-400 mb-8 leading-relaxed">Are you sure you want to delete <span className="text-white font-semibold">{projectInfo.title}</span>?</p>
                <div className="flex items-center justify-end gap-3"><button onClick={() => setShowDeleteModal(false)} disabled={isDeleting} className="px-5 py-2.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors font-medium">Cancel</button><button onClick={handleDeleteProject} disabled={isDeleting} className="px-5 py-2.5 rounded-lg bg-red-600 hover:bg-red-500 text-white shadow-lg shadow-red-900/20 transition-all font-bold flex items-center gap-2 disabled:bg-red-900 disabled:opacity-50">{isDeleting ? <Loader2 size={18} className="animate-spin" /> : null}{isDeleting ? "Deleting..." : "Yes, Delete It"}</button></div>
            </div>
        </div>
      )}
      {showSessionModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl max-w-md w-full shadow-2xl">
                <div className="flex items-center gap-4 mb-4 text-amber-500"><div className="p-3 bg-amber-500/10 rounded-full"><ShieldAlert size={32} /></div><h3 className="text-xl font-bold text-white">Nullify Sessions?</h3></div>
                <p className="text-slate-400 mb-8 leading-relaxed">This will disconnect the plugin.</p>
                <div className="flex items-center justify-end gap-3"><button onClick={() => setShowSessionModal(false)} className="px-5 py-2.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors font-medium">Cancel</button><button onClick={handleConfirmNullify} className="px-5 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-500 text-white shadow-lg shadow-amber-900/20 transition-all font-bold">Confirm & Reset</button></div>
            </div>
        </div>
      )}
    </div>
  );
}

function CollapsibleCode({ code, language = "Script" }) {
    const [expanded, setExpanded] = useState(false);
    const [copied, setCopied] = useState(false);
    const handleCopy = (e) => { e.stopPropagation(); navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); };
    return (
        <div className="my-3 rounded-lg overflow-hidden border border-slate-700 bg-[#0d1117] shadow-sm">
            <div className="flex items-center justify-between px-4 py-2 bg-slate-800/50 cursor-pointer hover:bg-slate-800 transition-colors select-none" onClick={() => setExpanded(!expanded)}>
                <div className="flex items-center gap-2 text-xs font-mono font-semibold text-blue-400"><FileCode size={14} /><span>{language}</span></div>
                <div className="flex items-center gap-3"><button onClick={handleCopy} className="text-slate-500 hover:text-white transition-colors">{copied ? <Check size={14} className="text-green-400"/> : <Copy size={14} />}</button><div className="flex items-center gap-1 text-slate-500 text-xs"><span>{expanded ? 'Collapse' : 'Expand'}</span>{expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</div></div>
            </div>
            {expanded && <div className="p-4 overflow-x-auto bg-[#0d1117]"><pre className="text-xs font-mono text-slate-300 whitespace-pre leading-relaxed">{code}</pre></div>}
        </div>
    );
}

function FormatInline({ text }) { const parts = text.split(/(\*\*.*?\*\*)/g); return <>{parts.map((part, i) => part.startsWith('**') && part.endsWith('**') ? <strong key={i} className="font-bold text-blue-200">{part.slice(2, -2)}</strong> : part)}</>; }

function ThinkingBubble({ content }) {
    const [expanded, setExpanded] = useState(false);
    return (
        <div className="my-2 p-2 rounded-lg bg-slate-900/50 border border-slate-800/50">
            <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-300 transition-colors w-full">
                <BrainCircuit size={14} />
                <span>Thinking Process...</span>
                {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            {expanded && (
                <div className="mt-2 pl-6 border-l-2 border-slate-800 text-xs text-slate-500 italic leading-relaxed whitespace-pre-wrap">
                    {content}
                </div>
            )}
        </div>
    );
}

function CollapsiblePM({ content }) {
    const [expanded, setExpanded] = useState(false);
    // Strip code blocks for preview
    const cleanContent = content.replace(/```[\s\S]*?```/g, "").trim();
    const sentences = cleanContent.match(/[^.!?]+[.!?]+[\])'"]*/g) || [cleanContent];
    const previewText = sentences.slice(0, 2).join(' ').trim();
    const hasMore = sentences.length > 2 || content.length > 300;

    return (
        <div className="transition-all">
            <div className="flex items-center justify-between mb-2"><div className="flex items-center gap-2 text-indigo-400 text-xs font-bold uppercase tracking-wider"><Briefcase size={12} /> Project Manager</div>{hasMore && (<button onClick={() => setExpanded(!expanded)} className="text-indigo-400 hover:text-indigo-300 text-xs flex items-center gap-1">{expanded ? 'Show Less' : 'Read More'} {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</button>)}</div>
            <div className="text-sm leading-relaxed">{expanded ? (<MessageRenderer content={content} isNested={true} />) : (<p><FormatInline text={previewText} />{hasMore && "..."}</p>)}</div>
        </div>
    );
}

function MessageRenderer({ content, isNested = false }) {
    if (!content) return null;

    if (!isNested && (content.startsWith('[PM RESPONSE]') || content.includes('PM RESPONSE:'))) {
        const cleanContent = content.replace(/\[PM RESPONSE\]:?|PM RESPONSE:/gi, '').trim();
        return <CollapsiblePM content={cleanContent} />;
    }

    if (content.includes('__THINK__')) {
        const parts = content.split('__THINK__');
        return (
            <div className="space-y-2">
                {parts.map((part, index) => {
                    if (index === 0 && part.trim() === "") return null;
                    if (index > 0) {
                        return <ThinkingBubble key={index} content={part} />;
                    }
                    return <MessageRenderer key={index} content={part} isNested={true} />;
                })}
            </div>
        );
    }

    const blocks = content.split(/(```(?:lua|luau|bash|json)?[\s\S]*?```)/g);
    return (
        <div className="space-y-2 text-sm md:text-base">
            {blocks.map((block, index) => {
                if (block.startsWith('```')) {
                    const firstLine = block.split('\n')[0];
                    const language = firstLine.replace('```', '').trim() || 'Script';
                    const code = block.replace(/^```.*\n?/, '').replace(/```$/, '');
                    return <CollapsibleCode key={index} code={code} language={language} />;
                }
                const lines = block.split('\n');
                return (
                    <div key={index} className="whitespace-pre-wrap leading-relaxed">
                        {lines.map((line, i) => {
                            const trimmed = line.trim();
                            if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) return <div key={i} className="flex items-start gap-2 ml-2 my-1"><span className="text-blue-500 mt-1.5">•</span><span className="text-slate-300"><FormatInline text={trimmed.substring(2)} /></span></div>;
                            if (/^\d+\.\s/.test(trimmed)) { const [num, ...rest] = trimmed.split('.'); return <div key={i} className="flex items-start gap-2 ml-2 my-1"><span className="text-blue-500 font-mono font-bold">{num}.</span><span className="text-slate-300"><FormatInline text={rest.join('.').trim()} /></span></div>; }
                            if (!trimmed) return <div key={i} className="h-2" />;
                            return <div key={i}><FormatInline text={line} /></div>;
                        })}
                    </div>
                );
            })}
        </div>
    );
}

function NavIcon({ icon, active, onClick, label }) { return <button onClick={onClick} className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 w-full text-left font-medium ${active ? 'bg-slate-800 text-blue-400' : 'text-slate-400 hover:text-slate-100 hover:bg-slate-800/50'}`}>{icon}<span>{label}</span></button>; }
function CredentialCopyBox({ value, isRolling }) {
    const [copied, setCopied] = useState(false);
    const displayValue = (value && !isRolling) ? (value.length > 8 ? value.slice(0, 8) + "..." : value) : (isRolling ? "Generating..." : "...");
    const handleCopy = () => { if (value && !isRolling) { navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 2000); } };
    return (
        <div className={`flex items-center gap-2 bg-slate-950 p-3 rounded-lg border transition-all duration-300 mt-2 ${isRolling ? 'border-blue-500/50 opacity-50' : 'border-slate-800'}`}>
            <div className="flex-1 font-mono text-slate-300 text-sm truncate tracking-widest">{displayValue}</div>
            <button onClick={handleCopy} disabled={isRolling || !value} className="p-2 hover:bg-slate-800 rounded text-slate-400 hover:text-white transition-colors relative disabled:opacity-0" title="Copy Key">{copied ? <Check size={18} className="text-green-400" /> : <Copy size={18} />}</button>
        </div>
    );
}
